/* eslint-disable no-useless-escape */
/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { AxiosRequestConfig } from 'axios'
import { CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { removePlusSign } from 'utils/functions/formatNumber'

import {
  Box,
  Button,
  Center,
  UnorderedList,
  Icon,
  ListItem,
  Text,
  VStack,
  FormLabel,
  FormControl,
  Image,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Flex,
  Checkbox,
} from '@chakra-ui/react'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { useSubscriberContext } from 'providers/SubscriberProvider'
import { useFormContext } from 'providers/FormProvider'
import countriesMock from 'services/mock/countries.json'
import { capitalize } from 'utils/functions/formatString'
import { isSuccessStatus } from 'utils/functions/validations'
import { getFileRequirement, getFileTerms } from 'services/api'
import useGa from 'hooks/ga/useGa'
import { IResponseGeneric, IReturnSendEmailRecoveryPasswordError } from 'services/IApi.d'
import {
  defaultUserFields,
  validUserFields,
  invalidUserFields,
  defaultPasswordFields,
  validPasswordFields,
  invalidPasswordFields
} from './subscriptionTypes'
import { IAditionalParams } from '../ISubscriptionUser.d'
import { ICountries } from 'providers/ISubscriberProvider'
import { STATUS_409 } from 'services/apiData'
import { generateFile, showErrorMessage, 
  validateConsecutiveChars, validateLength, validateOneNumberSpecialChar, validateOneUpperLowerCaseChars, 
  validatePersonalData, validateProgressiveChars, validateSpecialChars, validateUserDifferentToKey } from './registerUserData'
import { returnSendEmailRecoveryPasswordErrorType } from 'services/returnApiType'

import DotSVG from 'assets/images/dot.svg'
import EyeSVG from 'assets/images/eye.svg'
import EyeOffSVG from 'assets/images/eye-off.svg'


interface ErrorType {
  message: string,
  state: string
}

interface RegisterUserProps {
  innitialValues: Array<object>,
  type: string,
  displayName: string
  recoveryUser?: boolean
  recoveryPass?: boolean
}

interface JwtTokenValidate extends JwtPayload {
  canal?: string;
  fechaHoraSolicitud?: string;
  parametrosAdicionales?: string;
  requestId?: string;
}

const RegisterUser: React.FC<RegisterUserProps> = ({ innitialValues, type, displayName, recoveryUser, recoveryPass }) => {
  const dataSeis = 6
  const dataDoce = 12
  const data250 = 250
  
  const [showField, setShowField] = useState(false)
  const [showFieldIcon, setShowFieldIcon] = useState(false)
  const [showFieldConfirmation, setShowFieldConfirmation] = useState(false)
  const [showFieldConfirmationIcon, setShowFieldConfirmationIcon] = useState(false)
  const [isCheckedTyC, setIsCheckedTyC] = useState<boolean>(false)
  const [inProgress, setInProgress] = useState<boolean>(false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { fields, handleFieldChange, errors, handleFieldError } = useFormContext()
  const navigate = useNavigate()
  
  const { sendDataLayer } = useGa()
  const [validations, setValidations] = useState(innitialValues)
  const { subscriberData, setSubscriberData, saveData, flow, adminCollaboratorData, updatePassword, codeUser, setCodeUser } = useSubscriberContext()
  const { nombre: collaboratorName, apellido: collaboratorLastName, 
    codArea, telefono, nacionalidad, codPais, email, 
    numeroDocumento: collaboratorDocument, tipoDocumento: documentType, idColaborador: collaboratorId } = adminCollaboratorData
  const isAdminFlow = flow === 'administrator'
  const { name = '', lastName = '', user = '', documentForeign = '' } = subscriberData || {}
  const countries = countriesMock.data.paisList.filter(pais => pais.codDN)

  const companyCuit = localStorage.getItem('companyCuit')
  const idRepresentante = localStorage.getItem('idRepresentante')
  const company = subscriberData?.company || localStorage.getItem('empresa') || ''  
  const [searchParams] = useSearchParams()
  const code: string = searchParams.get('code')

  const titleUsuario = useMemo(() => recoveryUser ? 'Actualizá tus credenciales de acceso' : 'Vamos a crear tu cuenta', [recoveryUser])
  const titlePassword = useMemo(() => recoveryPass ? 'Actualizá tus credenciales de acceso' : '¡Ya falta menos!', [recoveryPass])
  const title = type === 'user' ? titleUsuario : titlePassword
  const subtitle = useMemo(() => type === 'user' ? '¿Qué usuario deseás utilizar?' : '¿Con qué contraseña ingresarás a Interbanking?', [type])
  const errorFieldMessage = useMemo(() => type === 'user' ? 'Los usuarios no coinciden' : 'Las contraseñas no coinciden', [type])
  const errorRequiredFieldMessage = 'Este campo es requerido'
  const buttonTextRecovery = useMemo(() => recoveryPass ? 'Actualizar' : 'Crear mi cuenta', [recoveryPass])
  const buttonText = type === 'user' ? 'Continuar' : buttonTextRecovery
  const errorFieldPasswordAge = 'La contraseña que ingresaste ya ha sido utilizada anteriormente'
  const navigateToPaswordPath = useMemo(() => recoveryUser ? '/suscripcion/recuperar-contrasena' : '/suscripcion/contrasena', [recoveryUser])


  const validateForm = () => {
    const { input, inputConfirmation } = fields
    const hasErrorState: unknown = validations && Object.values(validations).find((error: ErrorType) => error.state === 'error' || error.state === 'default')
    const hasErrors: any = errors && Object.values(errors).find((error: { message: '', isInvalid: false }) => error.isInvalid)
    const isEqual: boolean = input === inputConfirmation
    //
    const isValidResult = !hasErrorState && !hasErrors && input
    const isValidResultInput = input.length > 0 && inputConfirmation && inputConfirmation.length > 0
    return isValidResult && isValidResultInput && isEqual
  }

  const isDisabled: boolean = useMemo(() =>  (isAdminFlow && type === 'user') ? !isCheckedTyC : false 
    ,[isAdminFlow, type, isCheckedTyC]
  )
    
  const handleField = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (!/^\S*\s+\S*$/.test(value) || value.length === 0) {
      handleFieldChange(event)
    }
  }, [])

  const handleFieldConfirmation = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const { input, inputConfirmation } = fields
    if (!/^\S*\s+\S*$/.test(value) || value.length === 0) {
      handleFieldChange(event)
    }
    if (!input || !inputConfirmation) {
      return
    }
    if (value === input) {
      handleFieldError(event)
    } else if (Number(value) !== 0) {
      handleFieldError(event, { message: errorFieldMessage, isInvalid: true })
    } else {
      return
    }
  }, [fields, errorFieldMessage])

  const getNacionalidad = codeDN => countries.find((country: ICountries) => country.codDN === codeDN)?.nombre

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    //
    if (type === 'user') {
      sendDataLayer({ event: 'crear_usuario' })
      setSubscriberData({ ...subscriberData, user: fields.input })
      navigate(navigateToPaswordPath)
      return
    }
    //
    if (recoveryPass) {
      setInProgress(true)
    }
    let dataToSave
    let savedData
    if (isAdminFlow) {
      dataToSave = {
        ...subscriberData,
        password: fields.input,
        codArea,
        phone: removePlusSign(telefono),
        nacionalidad,
        nacionality: getNacionalidad(nacionalidad),
        name: collaboratorName,
        lastName: collaboratorLastName,
        codPais: removePlusSign(codPais),
        email,
        document: collaboratorDocument,
        documentType: documentType === 'PAS' ? 'Pasaporte' : 'Documento',
        collaboratorId
      }
      //
      const recaptchaTokenAdministrator: string = await executeRecaptcha('administrator')
      savedData = await saveData(dataToSave, recaptchaTokenAdministrator) // save admin
      if (!isSuccessStatus(savedData.status)) {
        navigate('/error')
        return
      }
      navigate('/suscripcion/creacion-exitosa', { state: 'administrator' })
      return
    }
    //
    dataToSave = { ...subscriberData, password: fields.input }
    setSubscriberData(dataToSave)
    //
    if (recoveryPass) {
      const decoded = jwtDecode<JwtTokenValidate>(codeUser)
      const { parametrosAdicionales } = decoded
      const splitData = parametrosAdicionales.split('&')
      const params: IAditionalParams = {}
      splitData.forEach(item => {
        const spliItem = item.split('=')
        // eslint-disable-next-line prefer-destructuring
        params[spliItem[0]] = spliItem[1]
      })
      const responseData = await updatePassword({ ...dataToSave, idSuscriptor: params.idSuscriptor })
      if (!isSuccessStatus(responseData.status)) {
        if (responseData.status === STATUS_409) {
          const sendEmailFail: IReturnSendEmailRecoveryPasswordError = responseData.data
          const sendEmailFailErr: returnSendEmailRecoveryPasswordErrorType = sendEmailFail.errors.shift().errorMessage
          navigate('/suscripcion/error-recuperar', { state: { pageBefore: 'recoveryUser409', messageType: sendEmailFailErr } })
          return
        }
        navigate('/suscripcion/error-recuperar', { state: { pageBefore: 'recoveryUser500' } })
        return
      }
      if (responseData.data?.data.errores?.length > 0) {
        showErrorMessage(handleFieldError, errorFieldPasswordAge)
        setInProgress(false)
        return
      }
      if (responseData.data?.data?.errores?.length === 0) {
        navigate('/suscripcion/exito')
        return
      }
    }
    //
    const recaptchaTokenSubscriptor: string = await executeRecaptcha('subscriptor')
    savedData = await saveData(dataToSave, recaptchaTokenSubscriptor) // save suscriptor
    if (!isSuccessStatus(savedData.status)) {
      navigate('/suscripcion/error-correo', {
        state: {
          pageBefore:
            'subscriptorFlow'
        }
      })
      return
    }
    sendDataLayer({ event: 'sign_up' })
    navigate('/suscripcion/creacion-exitosa')
  }

  const validateFields = event => {
    const { value } = event.target
    const { input } = fields
    const emptyValue = value.trim() === ''
    if (emptyValue) {
      handleFieldError(event, { message: errorRequiredFieldMessage, isInvalid: true })
    } else if (input !== value) {
      handleFieldError(event, { message: errorFieldMessage, isInvalid: true })
    } else {
      handleFieldError(event)
    }
  }
  
  const handleFieldConfirmationValidation = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    validateFields(event)
  }, [ fields, errorFieldMessage])

  const handleFieldValidation = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const emptyValue = value.trim() === ''
    validateFields(event)
    //
    switch (type) {
      case 'user':
        if (emptyValue) {
          return setValidations(prevState => ({ ...prevState,
            length: defaultUserFields.length,
            specialChars: defaultUserFields.specialChars,
          }))
        }
        setValidations(prevState => ({ ...prevState, 
            length: validateLength(value, dataSeis, dataDoce) ? validUserFields.length : invalidUserFields.length
          }))
        setValidations(prevState => ({ ...prevState,
            specialChars: validateSpecialChars(value)
          }))      
        break
      case 'password':
        if (emptyValue) {
          return setValidations(prevState => ({ ...prevState,
            length: defaultPasswordFields.length,
            leastOneSpecialChar: defaultPasswordFields.leastOneSpecialChar,
            leastOneUpperLowerCaseChars: defaultPasswordFields.leastOneUpperLowerCaseChars,
            consecutiveChars: defaultPasswordFields.consecutiveChars,
            progressiveChars: defaultPasswordFields.progressiveChars,
            personalData: defaultPasswordFields.personalData,
            userDifferentToKey: defaultPasswordFields.userDifferentToKey
          }))
        }
        setValidations(prevState => ({ ...prevState, length: validateLength(value, 8) ? validPasswordFields.length : invalidPasswordFields.length }))
        setValidations(prevState => ({ ...prevState, leastOneUpperLowerCaseChars: validateOneUpperLowerCaseChars(value) }))
        setValidations(prevState => ({ ...prevState, leastOneSpecialChar: validateOneNumberSpecialChar(value) }))
        setValidations(prevState => ({ ...prevState, consecutiveChars: validateConsecutiveChars(value) }))
        setValidations(prevState => ({ ...prevState, progressiveChars: validateProgressiveChars(value) }))
        setValidations(prevState => ({ ...prevState, personalData: validatePersonalData(value, name, documentForeign, lastName, company, collaboratorName, collaboratorLastName, type, isAdminFlow) }))
        setValidations(prevState => ({ ...prevState, userDifferentToKey: validateUserDifferentToKey(value, user) }))
        break
      default:
    }
    return event
  }, [type, fields,  errorFieldMessage, user, name, documentForeign, lastName, company, collaboratorName, collaboratorLastName, isAdminFlow])

  const handleFieldToggle = useCallback(() => {
    setShowField(!showField)
    setShowFieldIcon(!showFieldIcon)
  }, [showField, showFieldIcon])

  const handleFieldConfirmationToggle = useCallback(() => {
    setShowFieldConfirmation(!showFieldConfirmation)
    setShowFieldConfirmationIcon(!showFieldConfirmationIcon)
  }, [showFieldConfirmation, showFieldConfirmationIcon])

  const renderIcon = (state: string) => (
    <Icon as={state === 'check' ? CheckIcon : CloseIcon} color={state === 'check' ? 'success.600' : 'destructive.500'} />
  )

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => setIsCheckedTyC(e.target.checked)

  const downloadFileTermsCondition = useCallback(async() => {
    const docs = async (): Promise<IResponseGeneric> => {
      const token: string = await executeRecaptcha('Terms_and_conditions')
      const headers: AxiosRequestConfig<unknown> = { headers: { 'x-recaptcha-token': token } }
      return getFileTerms({ companyCuit, collaboratorId: idRepresentante }, headers)
    }
    docs().then(res => generateFile(res))
  }, [companyCuit, idRepresentante])

  const downloadFileRequirement = useCallback(async() => {  
    const docs = async (): Promise<IResponseGeneric> => {
      const token: string = await executeRecaptcha('technical_requirements')
      const headers: AxiosRequestConfig<unknown> = { headers: { 'x-recaptcha-token': token } }
      return getFileRequirement({ companyCuit, collaboratorId: idRepresentante }, headers)
    }
    docs().then(res => generateFile(res))
  }, [companyCuit, idRepresentante])    


  useEffect(() => {
    if (code) {
      setCodeUser(code)
    }
  }, [executeRecaptcha])


  return (
    <Box>
      <Box m='3rem 0'>
        <Center>
          <Text className='title' textStyle='h3'>
            {title}
          </Text>
        </Center>
        <Center>
          <Text className='sub-title' textStyle='titleMd'>
            {subtitle}
          </Text>
        </Center>
      </Box>
      <Box w='30%' m='0 auto'>
        <Box mt='70'>
          <form className='Form' onSubmit={handleSubmit}>
            <VStack spacing='16'>
              <FormControl isInvalid={errors.input ? errors.input.isInvalid : false}>
                <InputGroup>
                  <Input
                    value={fields.input}
                    onChange={handleField}
                    onBlur={handleFieldValidation}
                    required
                    name='input'
                    maxLength={type === 'user' ? dataDoce : data250}
                    className='input-disable-error-icon'
                    type={showField ? 'text' : 'password'}
                    placeholder={' '}
                    bg='white'
                    data-testid='input-test'
                  />
                  <FormLabel>
                    {capitalize(displayName)}
                  </FormLabel>
                  <InputRightElement className='form-input-field right-icon'
                    width='4.5rem' onClick={handleFieldToggle}>
                    {showFieldIcon ? <Image alt='Show input image' color='neutral.110'
                      src={EyeSVG} /> : <Image alt='Hide input image' color='neutral.110'
                        src={EyeOffSVG} />}
                  </InputRightElement>
                </InputGroup>
                {errors.input && <FormErrorMessage>{errors.input.message}</FormErrorMessage>}
              </FormControl>
              <FormControl isInvalid={errors.inputConfirmation ? errors.inputConfirmation.isInvalid : false}>
                <InputGroup>
                  <Input
                    value={fields.inputConfirmation}
                    onChange={handleFieldConfirmation}
                    onBlur={handleFieldConfirmationValidation}
                    required
                    name='inputConfirmation'
                    maxLength={type === 'user' ? dataDoce : data250}
                    className='input-disable-error-icon'
                    type={showFieldConfirmation ? 'text' : 'password'}
                    placeholder=" "
                    bg='white'
                    data-testid='inputConfirmation-test'
                  />
                  <FormLabel>
                    Confirmar {displayName}
                  </FormLabel>
                  <InputRightElement className='right-icon' width='4.5rem'
                    onClick={handleFieldConfirmationToggle}>
                    {showFieldConfirmationIcon ? <Image alt='Show input image'
                      src={EyeSVG} /> : <Image alt='Hide input image' src={EyeOffSVG} />}
                  </InputRightElement>
                </InputGroup>
                {errors.inputConfirmation && <FormErrorMessage>{errors.inputConfirmation.message}</FormErrorMessage>}
              </FormControl>
            </VStack>
            <Box w='100%'>
              <Text mb='6' mt='6' className='link'>{`Para tu ${displayName} considerá `}</Text>
              <UnorderedList className='list'>
                {Object.values(validations).map((validation: any) => (
                  <ListItem mb='4' style={{ 'alignItems': 'baseline' }} className='list-item' key={validation.message}>
                    {validation.state === 'default' && <Image m='1' paddingBottom='2px' className="listitem__dot" alt='Show input image' src={DotSVG} /> || renderIcon(validation.state)}
                    <Text ml='2' color={validation.state === 'default' ? 'secondary.500' : ''}>
                      {validation.message} </Text>
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
            {isAdminFlow && type === 'user' &&
              <Box w='101%'>
                <Flex justifyContent='center' alignItems='center' mt={14}>
                  <Checkbox
                    isChecked={isCheckedTyC}
                    onChange={e => handleCheckbox(e)}
                    data-testid='checkTyC'
                    pr={3}
                    role='checkbox'
                  />
                  <Box w='100%' fontSize='14px' lineHeight='20px' letterSpacing='0.1px' pt={4}>
                    Al continuar acepto los <Button variant='primary-text' onClick={downloadFileTermsCondition}>Términos y condiciones</Button> y los <Button variant='primary-text' onClick={downloadFileRequirement}>Requisitos técnicos y de seguridad</Button>
                  </Box>
                </Flex>
              </Box>
            }
            <Center w='100%'>
              <Button
                disabled={!validateForm() || isDisabled || inProgress}
                type='submit'
                data-testid='submit-button'
                className='submit-button'
                mt='9'
                mb='10'
                width='50%'
                color='primary.500'
              >
                {buttonText}
              </Button>
            </Center>
          </form>
        </Box>
      </Box>
    </Box>
  )
}
export default RegisterUser
